//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { IAliasedDateRange } from '@components/date-range-picker/dateRangePickerModels';
import { SocialNetwork } from '@models/SocialNetwork';
import { useSocialConnections } from '@reporting/hooks/useSocialConnections';
import {
  PinterestRestrictions,
  canFetchPinterestAudienceData,
  canFetchPinterestEngagementData,
} from '@reporting/utils/pinterestReportingUtils';

/**
 * Centralized hook for Pinterest reporting capabilities.
 *
 * Use this hook in any component that needs to:
 * - Check if Pinterest data can be fetched
 * - Show Pinterest-specific alerts
 * - Determine what Pinterest features to enable
 *
 */
export const useCheckPinterestRestrictions = (
  dateRange: IAliasedDateRange,
): PinterestRestrictions => {
  const { connections } = useSocialConnections();
  const isPinterestConnected = connections[SocialNetwork.Pinterest].isConnected;
  const canFetchAudienceData = canFetchPinterestAudienceData(
    dateRange,
    isPinterestConnected,
  );
  const canFetchEngagementData = canFetchPinterestEngagementData(
    dateRange,
    isPinterestConnected,
  );

  return {
    canFetchAudienceData,
    canFetchEngagementData,
  };
};
